import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyPartnerTableColumnFilters,
  applyPartnerTableSort,
  isPartnerTableFilterValueActive,
} from '../Utils/partnerTableSortFilterUtils';

export const usePartnerTableSortFilter = (projects, {
  getColumnValue,
  getSortValue,
  initialSort = null,
  initialColumnFilters = null,
}) => {
  const [columnFilters, setColumnFilters] = useState(initialColumnFilters || {});
  const [sort, setSort] = useState(initialSort || { key: null, direction: null });

  const initialColumnFiltersKey = useMemo(
    () => JSON.stringify(initialColumnFilters || {}),
    [initialColumnFilters],
  );

  useEffect(() => {
    setColumnFilters(initialColumnFilters || {});
  }, [initialColumnFiltersKey, initialColumnFilters]);

  const setColumnFilter = useCallback((columnKey, value) => {
    setColumnFilters((current) => ({
      ...current,
      [columnKey]: value,
    }));
  }, []);

  const applyColumnFilters = useCallback((filters) => {
    setColumnFilters(filters || {});
  }, []);

  const resetColumnFilters = useCallback(() => {
    setColumnFilters(initialColumnFilters || {});
  }, [initialColumnFilters]);

  const hasActiveColumnFilters = useMemo(
    () => Object.values(columnFilters).some((value) => isPartnerTableFilterValueActive(value)),
    [columnFilters],
  );

  const toggleSort = useCallback((columnKey) => {
    setSort((current) => {
      if (current.key !== columnKey) {
        return { key: columnKey, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key: columnKey, direction: 'desc' };
      }
      return { key: null, direction: null };
    });
  }, []);

  const processedProjects = useMemo(() => {
    const filtered = applyPartnerTableColumnFilters(projects, columnFilters, getColumnValue);
    return applyPartnerTableSort(
      filtered,
      sort.key,
      sort.direction,
      getColumnValue,
      getSortValue,
    );
  }, [projects, columnFilters, sort.key, sort.direction, getColumnValue, getSortValue]);

  const columnFiltersKey = useMemo(
    () => JSON.stringify(columnFilters),
    [columnFilters],
  );

  const sortKeyValue = `${sort.key || ''}:${sort.direction || ''}`;

  return {
    columnFilters,
    setColumnFilter,
    applyColumnFilters,
    resetColumnFilters,
    hasActiveColumnFilters,
    sortKey: sort.key,
    sortDirection: sort.direction,
    toggleSort,
    processedProjects,
    columnFiltersKey,
    sortKeyValue,
  };
};
